import { createContext, useContext } from "react";
import type { Session, User } from "@supabase/supabase-js";

export type AuthRecoveryState = "ready" | "refreshing" | "retry_later" | "reauth_required";
export type AuthRecoveryOutcome = "recovered" | "retry_later" | "reauth_required";

export interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAuthor: boolean;
  isAuthorLoading: boolean;
  isAnonymous: boolean;
  anonymousTransferPending: boolean;
  anonymousTransferError: string | null;
  retryAnonymousTransfer: () => Promise<boolean>;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<boolean>;
  authRecoveryState: AuthRecoveryState;
  recoverSession: () => Promise<AuthRecoveryOutcome>;
  /** Retries only a confirmed HTTP 401 once. Network failures are never replayed. */
  runAuthenticatedRequest: <T>(request: () => Promise<T>) => Promise<T>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

const defaultAuthContext: AuthContextType = {
  user: null,
  session: null,
  loading: true,
  isAuthor: false,
  isAuthorLoading: true,
  isAnonymous: false,
  anonymousTransferPending: false,
  anonymousTransferError: null,
  retryAnonymousTransfer: async () => false,
  signOut: async () => {},
  refreshSession: async () => false,
  authRecoveryState: "ready",
  recoverSession: async () => "reauth_required",
  runAuthenticatedRequest: async <T,>(request: () => Promise<T>) => request(),
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    console.warn("useAuth called outside AuthProvider - using default context");
    return defaultAuthContext;
  }
  return context;
};
